import React, { useState, useEffect } from 'react';
import { Filter, Download, Briefcase, CheckCircle, RefreshCw, Clock, Pause, XCircle, FileText, Truck, PackageCheck, RotateCcw, Loader, X, Package, ShieldCheck } from 'lucide-react';
import StateMapWidget from '../components/StateMapWidget';
import { dashboardApi, fetchApiDirect } from '../services/api';
import { formatCurrency } from '../utils/helpers';
import { message, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { useTheme } from '../context/ThemeContext';

const { RangePicker } = DatePicker;

function DashboardOrder() {
  const { currentTheme } = useTheme();

  const getDefaultStates = () => [
    'Uttar Pradesh', 'Maharashtra', 'Rajasthan', 'Bihar', 'Madhya Pradesh',
    'Gujarat', 'Haryana', 'Punjab', 'Delhi', 'Jharkhand', 'West Bengal',
    'Assam Bengal', 'Karnataka', 'Chhattisgarh', 'Uttarakhand', 'Himachal Pradesh'
  ];

  const defaultStatesWithZero = getDefaultStates().map(state => ({
    state, count: 0, percentage: 0, performance: 'LOW'
  }));

  const [orderSummary, setOrderSummary] = useState(null);
  const [completedByState, setCompletedByState] = useState(defaultStatesWithZero);
  const [deliveredByState, setDeliveredByState] = useState(defaultStatesWithZero);
  const [loading, setLoading] = useState(true);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const getTodayDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const todayStr = getTodayDateString();

  const [filterParams, setFilterParams] = useState({ startDate: todayStr, endDate: todayStr, status: '', states: [], floorId: '', floorName: '', teamId: '', teamName: '' });
  const [tempFilterParams, setTempFilterParams] = useState({ startDate: todayStr, endDate: todayStr, status: '', states: [], floorId: '', floorName: '', teamId: '', teamName: '' });

  // ── Floor → Team dynamic state ──────────────────────────────────────────
  const [floors,       setFloors]       = useState([]);
  const [teams,        setTeams]        = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);

  useEffect(() => {
    // Floors load on mount
    fetchApiDirect('/floors').then(res => {
      const arr = Array.isArray(res) ? res : res?.content || res?.data || [];
      setFloors(arr.map(f => ({ id: String(f.id || f.floorId || ''), name: f.floorName || f.name || '' })).filter(f => f.id));
    }).catch(() => {});
  }, []);

  const handleFloorChange = async (floorId, floorName) => {
    setTempFilterParams(prev => ({ ...prev, floorId, floorName, teamId: '', teamName: '' }));
    setTeams([]);
    if (!floorId) return;
    setTeamsLoading(true);
    try {
      const res = await fetchApiDirect(`/employees/grouped?floorId=${floorId}`);
      // API se jo bhi format aaye handle karo
      const teamsRaw = Array.isArray(res?.teams)    ? res.teams
                     : Array.isArray(res?.data)      ? res.data
                     : Array.isArray(res?.content)   ? res.content
                     : Array.isArray(res)             ? res
                     : [];
      console.log('Teams API response:', res); // debug
      const mappedTeams = teamsRaw.map(t => ({
        id:   String(t.teamId || t.id || t.team_id || ''),
        name: t.teamName || t.name || t.team_name || '',
      })).filter(t => t.id && t.name);
      setTeams(mappedTeams);
    } catch (e) { console.error(e); }
    finally { setTeamsLoading(false); }
  };
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, [filterParams]);

  const buildQuery = (filters) => {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.status) params.append('status', filters.status);
    if (filters.floorId) params.append('floorId', filters.floorId);
    if (filters.teamId)  params.append('teamId',  filters.teamId);
    if (filters.states && filters.states.length > 0) {
      filters.states.forEach(s => params.append('states', s));
    }
    return params.toString() ? `?${params}` : '';
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0';
    return Number(num).toLocaleString();
  };

  const buildStateBreakdown = (stateData) => {
    if (!Array.isArray(stateData) || stateData.length === 0) return defaultStatesWithZero;
    return stateData.slice(0, 16).map(item => ({
      state: item.state,
      count: item.count || 0,
      percentage: item.percentage || 0,
      performance: item.performance || 'LOW'
    }));
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const query = buildQuery(filterParams);

      const [summaryResponse, byStateResponse] = await Promise.all([
        dashboardApi.getOrderSummary(query),
        dashboardApi.getOrdersByState(
          filterParams.status ? { status: filterParams.status } : {}
        )
      ]);

      if (summaryResponse) {
        setOrderSummary(summaryResponse);
      } else {
        setOrderSummary({});
      }

      if (byStateResponse?.states) {
        const statesObj = byStateResponse.states;

        const validStates = Object.entries(statesObj).filter(([state]) =>
          state.trim() !== '' &&
          state.length > 2 &&
          /^[A-Za-z\s&]+$/.test(state.trim())
        );

        const confirmedArray = validStates
          .map(([state, data]) => {
            const verifiedCount = data.verified || 0;
            return {
              state: state.trim(),
              count: verifiedCount,
              percentage: parseFloat((data.verifiedPercentage || 0).toFixed(1)),
              performance: (data.verifiedPercentage || 0) >= 15 ? 'HIGH'
                         : (data.verifiedPercentage || 0) >= 7  ? 'MID'
                         : 'LOW'
            };
          })
          .filter(item => item.count > 0)
          .sort((a, b) => b.count - a.count);

        const deliveredArray = validStates
          .map(([state, data]) => {
            const deliveredCount = data.delivered || 0;
            return {
              state: state.trim(),
              count: deliveredCount,
              percentage: parseFloat((data.deliveredPercentage || 0).toFixed(1)),
              performance: (data.deliveredPercentage || 0) >= 20 ? 'HIGH'
                         : (data.deliveredPercentage || 0) >= 8  ? 'MID'
                         : 'LOW'
            };
          })
          .filter(item => item.count > 0)
          .sort((a, b) => b.count - a.count);

        setCompletedByState(buildStateBreakdown(confirmedArray));
        setDeliveredByState(buildStateBreakdown(deliveredArray));
      }
    } catch (error) {
      console.error('Error fetching order dashboard data:', error);
      setOrderSummary({});
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClick = () => {
    setTempFilterParams({ ...filterParams });
    setShowFilterModal(true);
  };

  const handleApplyFilters = () => {
    setFilterParams({ ...tempFilterParams });
    setShowFilterModal(false);
  };

  const handleClearFilters = () => {
    const clearedFilters = { startDate: todayStr, endDate: todayStr, status: '', states: [], floorId: '', floorName: '', teamId: '', teamName: '' };
    setFilterParams(clearedFilters);
    setTempFilterParams(clearedFilters);
    setTeams([]);
    setShowFilterModal(false);
  };

  const handleFrontendExport = () => {
    try {
      setExporting(true);
      let csvContent = "--- ORDER SUMMARY ---\n";
      csvContent += "Label,Count,Total Value,Avg Value\n";
      orderCards.forEach(card => {
        csvContent += `"${card.label}",${card.count},${card.totalValue},${card.avgValue.toFixed(2)}\n`;
      });
      csvContent += "\n\n--- ORDER COMPLETED (STATE WISE) ---\n";
      csvContent += "State,Count,Percentage,Performance\n";
      completedByState.forEach(item => {
        csvContent += `"${item.state}",${item.count},${item.percentage}%,${item.performance}\n`;
      });
      csvContent += "\n\n--- ORDER DELIVERED (STATE WISE) ---\n";
      csvContent += "State,Count,Percentage,Performance\n";
      deliveredByState.forEach(item => {
        csvContent += `"${item.state}",${item.count},${item.percentage}%,${item.performance}\n`;
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute("href", url);
      link.setAttribute("download", `Order_Dashboard_Report_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('Report downloaded successfully!');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to generate report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const hasActiveFilters = () => {
    return filterParams.startDate || filterParams.endDate || filterParams.status ||
      filterParams.floorId || filterParams.teamId ||
      (filterParams.states && filterParams.states.length > 0);
  };

  const orderCards = [
    { label: 'Total Orders',     count: orderSummary?.totalOrders        || 0, totalValue: orderSummary?.totalValue          || 0, avgValue: orderSummary?.avgValue              || 0, color: 'bg-[#3B82F6]', icon: <Briefcase    className="w-5 h-5" /> },
    // ── ADD: Confirmed Orders card ──────────────────────────────────────────
    { label: 'Confirmed Orders', count: orderSummary?.confirmedOrders     || 0, totalValue: orderSummary?.confirmedValue      || 0, avgValue: orderSummary?.confirmedAvgValue     || 0, color: 'bg-[#4B9EF9]', icon: <ShieldCheck  className="w-5 h-5" /> },
    // ────────────────────────────────────────────────────────────────────────
    { label: 'Verified Orders',  count: orderSummary?.verifiedOrders      || 0, totalValue: orderSummary?.verifiedValue       || 0, avgValue: orderSummary?.verifiedAvgValue      || 0, color: 'bg-[#10B981]', icon: <CheckCircle  className="w-5 h-5" /> },
    { label: 'Process Orders',   count: orderSummary?.processOrders       || 0, totalValue: orderSummary?.processValue        || 0, avgValue: orderSummary?.processAvgValue       || 0, color: 'bg-[#F59E0B]', icon: <RefreshCw    className="w-5 h-5" /> },
    { label: 'Pending Orders',   count: orderSummary?.pendingOrders       || 0, totalValue: orderSummary?.pendingValue        || 0, avgValue: orderSummary?.pendingAvgValue       || 0, color: 'bg-[#F97316]', icon: <Clock        className="w-5 h-5" /> },
    { label: 'Hold Orders',      count: orderSummary?.holdOrders          || 0, totalValue: orderSummary?.holdValue           || 0, avgValue: orderSummary?.holdAvgValue          || 0, color: 'bg-[#64748B]', icon: <Pause        className="w-5 h-5" /> },
    { label: 'Rejected Orders',  count: orderSummary?.rejectedOrders      || 0, totalValue: orderSummary?.rejectedValue       || 0, avgValue: orderSummary?.rejectedAvgValue      || 0, color: 'bg-[#EF4444]', icon: <XCircle      className="w-5 h-5" /> },
    { label: 'Draft Orders',     count: orderSummary?.draftOrders         || 0, totalValue: orderSummary?.draftValue          || 0, avgValue: orderSummary?.draftAvgValue         || 0, color: 'bg-[#B45309]', icon: <FileText     className="w-5 h-5" /> },
    { label: 'Dispatched',       count: orderSummary?.dispatchedOrders        , totalValue: orderSummary?.dispatchedValue         , avgValue: orderSummary?.dispatchedAvgValue       , color: 'bg-[#0EA5E9]', icon: <Package      className="w-5 h-5" /> },
    { label: 'Ready to Ship',    count: orderSummary?.readyToShipOrders       , totalValue: orderSummary?.readyToShipValue        , avgValue: orderSummary?.readyToShipAvgValue      , color: 'bg-[#F97316]', icon: <Clock        className="w-5 h-5" /> },
    { label: 'Shipped',          count: orderSummary?.shippedOrders           , totalValue: orderSummary?.shippedValue            , avgValue: orderSummary?.shippedAvgValue          , color: 'bg-[#10B981]', icon: <Truck        className="w-5 h-5" /> },
    { label: 'OFD Orders',       count: orderSummary?.ofdOrders           || 0, totalValue: orderSummary?.ofdValue            || 0, avgValue: orderSummary?.ofdAvgValue           || 0, color: 'bg-[#06B6D4]', icon: <Truck        className="w-5 h-5" /> },
    { label: 'Delivered Orders', count: orderSummary?.deliveredOrders     || 0, totalValue: orderSummary?.deliveredValue      || 0, avgValue: orderSummary?.deliveredAvgValue     || 0, color: 'bg-[#14B8A6]', icon: <PackageCheck className="w-5 h-5" /> },
    { label: 'RTO Orders',       count: orderSummary?.rtoOrders           || 0, totalValue: orderSummary?.rtoValue            || 0, avgValue: orderSummary?.rtoAvgValue           || 0, color: 'bg-[#8B5CF6]', icon: <RotateCcw    className="w-5 h-5" /> },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-teal-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading order dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h1 className="text-3xl font-bold text-gray-800">Order</h1>
          {hasActiveFilters() && (
            <div className="flex items-center space-x-2 flex-wrap">
              {filterParams.startDate && <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">From: {new Date(filterParams.startDate).toLocaleDateString()}</span>}
              {filterParams.endDate && <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">To: {new Date(filterParams.endDate).toLocaleDateString()}</span>}
              {filterParams.status && <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">Status: {filterParams.status}</span>}
              {filterParams.floorName && <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm">Floor: {filterParams.floorName}</span>}
              {filterParams.teamName  && <span className="px-3 py-1 bg-violet-100 text-violet-800 rounded-full text-sm">Team: {filterParams.teamName}</span>}
              {filterParams.states?.length > 0 && <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">States: {filterParams.states.length}</span>}
            </div>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={handleFilterClick} className={`p-2 border rounded-lg transition-colors ${hasActiveFilters() ? 'border-blue-500 bg-blue-50 text-blue-600' : 'border-gray-300 hover:bg-gray-50'}`} title="Filter">
            <Filter className="w-5 h-5" />
          </button>
          <button onClick={handleFrontendExport} disabled={exporting} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" title="Export">
            {exporting ? <Loader className="w-5 h-5 text-gray-600 animate-spin" /> : <Download className="w-5 h-5 text-gray-600" />}
          </button>
        </div>
      </div>

      {/* Filter Modal */}
      {showFilterModal && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowFilterModal(false);
          }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">Filter Dashboard</h2>
              <button onClick={() => setShowFilterModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                <div className="space-y-3">
                  <RangePicker
                    value={
                      tempFilterParams.startDate && tempFilterParams.endDate
                        ? [dayjs(tempFilterParams.startDate), dayjs(tempFilterParams.endDate)]
                        : null
                    }
                    onChange={(dates) => {
                      if (dates && dates[0] && dates[1]) {
                        setTempFilterParams(prev => ({
                          ...prev,
                          startDate: dates[0].format('YYYY-MM-DD'),
                          endDate: dates[1].format('YYYY-MM-DD'),
                        }));
                      } else {
                        setTempFilterParams(prev => ({
                          ...prev,
                          startDate: '',
                          endDate: '',
                        }));
                      }
                    }}
                    format="DD-MM-YYYY"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '10px' }}
                    size="large"
                  />
                  {/* Quick Preset Buttons */}
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: 'Today', getValue: () => { const t = dayjs().format('YYYY-MM-DD'); return [t, t]; } },
                      { label: 'Yesterday', getValue: () => { const y = dayjs().subtract(1, 'day').format('YYYY-MM-DD'); return [y, y]; } },
                      { label: 'Last 7 Days', getValue: () => [dayjs().subtract(6, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')] },
                      { label: 'This Month', getValue: () => [dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().endOf('month').format('YYYY-MM-DD')] },
                      { label: 'This Year', getValue: () => [dayjs().startOf('year').format('YYYY-MM-DD'), dayjs().endOf('year').format('YYYY-MM-DD')] },
                    ].map(preset => (
                      <button
                        key={preset.label}
                        type="button"
                        onClick={() => {
                          const [s, e] = preset.getValue();
                          setTempFilterParams(prev => ({ ...prev, startDate: s, endDate: e }));
                        }}
                        className="px-2.5 py-1 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              {/* ── Floor filter ── */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Floor</label>
                <select
                  value={tempFilterParams.floorId}
                  onChange={(e) => {
                    const floor = floors.find(f => f.id === e.target.value);
                    handleFloorChange(e.target.value, floor?.name || '');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Floors</option>
                  {floors.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>

              {/* ── Team filter — floor select hone ke baad populate ── */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team {teamsLoading && <span className="ml-1 inline-block w-3 h-3 border border-t-transparent rounded-full animate-spin align-middle border-blue-500" />}
                </label>
                <select
                  value={tempFilterParams.teamId}
                  onChange={(e) => {
                    const team = teams.find(t => t.id === e.target.value);
                    setTempFilterParams(prev => ({ ...prev, teamId: e.target.value, teamName: team?.name || '' }));
                  }}
                  disabled={!tempFilterParams.floorId || teamsLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">{teamsLoading ? 'Loading teams...' : 'All Teams'}</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">States (Select multiple)</label>
                <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
                  <div className="grid grid-cols-2 gap-2">
                    {getDefaultStates().map((state) => (
                      <label key={state} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input type="checkbox" checked={tempFilterParams.states.includes(state)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTempFilterParams({ ...tempFilterParams, states: [...tempFilterParams.states, state] });
                            } else {
                              setTempFilterParams({ ...tempFilterParams, states: tempFilterParams.states.filter(s => s !== state) });
                            }
                          }}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{state}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button onClick={handleClearFilters} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Clear Filters</button>
                <button onClick={handleApplyFilters} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">Apply Filters</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {orderCards.map((card, index) => {
          const hexMatch = card.color.match(/#([A-Fa-f0-9]+)/);
          const hex = hexMatch ? `#${hexMatch[1]}` : '#3B82F6';

          return (
            <div
              key={index}
              className="rounded-[22px] p-4 border shadow-sm flex flex-col justify-between min-h-[135px] transition-all hover:scale-[1.02] active:scale-95 group"
              style={{
                backgroundColor: `${hex}1A`,
                borderColor: `${hex}4D`,
                boxShadow: `0 8px 16px -8px ${hex}66`
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <div
                  className="p-3 rounded-[16px] flex items-center justify-center shrink-0 shadow-lg shadow-black/5 transition-transform group-hover:rotate-6 text-white"
                  style={{ backgroundColor: hex }}
                >
                  {React.cloneElement(card.icon, { size: 22, strokeWidth: 3 })}
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-black text-gray-500/80 uppercase tracking-widest mb-1">{card.label}</div>
                  <div className="text-[26px] font-black text-gray-800 leading-none tracking-tight">{formatNumber(card.count)}</div>
                </div>
              </div>
              <div className="border-t border-gray-200/50 pt-3 mt-1 space-y-1">
                <div className="flex justify-between items-center text-[11px] font-black">
                  <span className="text-gray-500/70 uppercase">Total:</span>
                  <span className="text-gray-800">{formatCurrency(card.totalValue)}</span>
                </div>
                <div className="flex justify-between items-center text-[11px] font-black">
                  <span className="text-gray-500/70 uppercase">Avg:</span>
                  <span className="text-gray-800">{formatCurrency(card.avgValue)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* State-wise Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="h-[900px]">
          <StateMapWidget title="Verified completed (state wise)" statusFilter="CONFIRMED" data={completedByState} />
        </div>
        <div className="h-[900px]">
          <StateMapWidget title="Order delivered (state wise)" statusFilter="DELIVERED" data={deliveredByState} />
        </div>
      </div>
    </div>
  );
}

export default DashboardOrder;